import React from 'react'

const CustomTextField = ({
	type,
	name,
	value,
	onChange,
	placeholder,
	label,
	required,
	toggleVisibility,
	icon,
	tooltipText,
	maxLength,
	error,
	disabled,
	rows = 3,
	charType,
}) => {
	const inputRef = React.useRef(null)

	// Initialize tooltips after the component mounts
	React.useEffect(() => {
		const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-toggle="tooltip"]'))
		const tooltipList = tooltipTriggerList.map((tooltipTriggerEl) => {
			return new window.bootstrap.Tooltip(tooltipTriggerEl)
		})
		return () => {
			tooltipList.forEach((tooltip) => tooltip.dispose())
		}
	}, [])

	// Prevent scrolling behavior for number inputs
	React.useEffect(() => {
		const input = inputRef.current
		if (input && type === 'number') {
			const preventScroll = (e) => {
				e.preventDefault()
				input.blur()
			}
			input.addEventListener('wheel', preventScroll, { passive: false })
			return () => {
				input.removeEventListener('wheel', preventScroll)
			}
		}
	}, [type])

	const hasError = error?.length > 0

	// Handle the change event for strictly alphabetic text input
	const handleTextChange = (e) => {
		const regex = /^[a-zA-Z\s]*$/ // Allows only letters and spaces
		const value = e.target.value

		if (regex.test(value) || value === '') {
			onChange(e)
		}
	}

	// Handle alphanumeric input for default text type, allowing special characters
	const handleAlphanumericChange = (e) => {
		const regex = /^[a-zA-Z0-9\s!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?]*$/ // Allows letters, numbers, spaces, and special characters
		const value = e.target.value

		if (regex.test(value) || value === '') {
			onChange(e)
		}
	}

	// Handle unrestricted input for password fields
	// Handle input specifically for password fields, allowing only !, %, and *
	// Handle input specifically for password fields, allowing only !, %, and *
	const handlePasswordChange = (e) => {
		const inputValue = e.target.value

		// Define a regex that allows only alphanumeric characters and specific special characters !, %, and *
		const allowedCharsRegex = /^[a-zA-Z0-9!*%]*$/
		// Check if input matches allowed pattern and prevent other characters
		if (allowedCharsRegex.test(inputValue)) {
			onChange(e)
		}
	}

	// Handle change event for strictly numeric input
	const handleNumberChange = (e) => {
		const { value } = e.target
		const maxLengthValue = maxLength || 0

		if (/^\d*$/.test(value) && value.length <= maxLengthValue) {
			onChange(e)
		}
	}
	// Custom validation for email field
	// Custom validation for email field
	const handleEmailChange = (e) => {
		const inputValue = e.target.value
		const maxNumberLength = 6

		// Split at the @ to handle before and after separately
		const [beforeAt, afterAt] = inputValue.split('@')

		// Define regex for each part
		const beforeAtRegex = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~\s.]*$/ // Now allows dots (.) in the local part before @
		const afterAtRegex = /^[a-zA-Z0-9.-]*$/ // Allows letters, digits, dots, and hyphens in the domain part

		// Check before @ part: max 6 digits and valid characters (including dots)
		const beforeAtIsValid = beforeAtRegex.test(beforeAt) && (beforeAt.match(/\d/g) || []).length <= maxNumberLength

		// Check after @ part if present: only valid characters (letters, digits, dots, hyphens)
		const afterAtIsValid = !afterAt || afterAtRegex.test(afterAt)

		// Check overall length and validate parts
		if (inputValue.length <= maxLength && beforeAtIsValid && afterAtIsValid) {
			onChange(e)
		}
	}

	const handleAlphaNumericWithDotChange = (e) => {
		const regex = /^[a-zA-Z0-9.\s]*$/ // Allows letters, numbers, periods, and spaces
		const value = e.target.value

		if (regex.test(value) || value === '') {
			onChange(e)
		}
	}

	return (
		<div className='mb-2'>
			<label
				className='form-label fw-bold'
				style={{
					color: 'black',
				}}
			>
				{label} {required && <span className='text-danger'>*</span>}
				{tooltipText && tooltipText.length > 0 && (
					<span className='ms-1' data-toggle='tooltip' data-placement='right' title={tooltipText}>
						<i className='bi bi-info-circle' style={{ color: 'rgb(251, 140, 0)' }}></i>
					</span>
				)}
			</label>
			<div className='input-group'>
				{type === 'textarea' ? (
					<textarea
						name={name}
						className='form-control'
						value={value}
						onChange={onChange}
						placeholder={placeholder}
						aria-label={label}
						style={{
							border: `1px solid ${hasError ? 'red' : '#d2d6da'}`,
							paddingLeft: '10px',
							borderRadius: '10px',
						}}
						maxLength={maxLength}
						disabled={disabled}
						rows={rows}
					/>
				) : type === 'date' ? (
					<input
						type='date'
						name={name}
						className='form-control'
						value={value}
						onChange={onChange}
						placeholder={placeholder}
						aria-label={label}
						style={{
							border: `1px solid ${hasError ? 'red' : '#d2d6da'}`,
							paddingLeft: '10px',
							borderRadius: '10px',
						}}
						disabled={disabled}
					/>
				) : (
					<input
						ref={inputRef}
						type={type}
						name={name}
						className='form-control'
						value={value}
						onChange={
							type === 'number'
								? handleNumberChange
								: type === 'email'
								? handleEmailChange
								: (type === 'password' || type === 'text') && charType === 'password'
								? handlePasswordChange
								: type === 'text' && charType === 'text'
								? handleTextChange
								: type === 'text' && charType === 'alphaNum'
								? handleAlphaNumericWithDotChange // Only alphanumeric with '.'
								: type === 'text' && !charType
								? handleAlphanumericChange // Allow alphanumeric and other special characters if no charType is specified
								: onChange // Default fallback for other types
						}
						placeholder={placeholder}
						aria-label={label}
						style={{
							border: `1px solid ${hasError ? 'red' : '#d2d6da'}`,
							paddingLeft: '12px',
							borderRadius: '10px',
						}}
						maxLength={maxLength}
						disabled={disabled}
						inputMode={type === 'number' ? 'numeric' : undefined}
						onKeyDown={(e) => {
							if (type === 'number' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
								e.preventDefault()
							}
						}}
					/>
				)}
				{icon && type !== 'textarea' && (
					<button
						className='btn btn-outline-secondary'
						type='button'
						onClick={toggleVisibility}
						style={{
							border: 'none',
							position: 'absolute',
							right: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							pointerEvents: 'auto',
							zIndex: 10,
							borderRadius: '10px',
						}}
						disabled={disabled}
					>
						{icon}
					</button>
				)}
			</div>
			{hasError && error && <div className='text-danger mt-1'>{error}</div>}
		</div>
	)
}

export default CustomTextField
